import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { FocusHeader } from '../components/FocusHeader';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Wand2, ArrowRight } from 'lucide-react';

interface Blog {
  id: string;
  slug: string;
  title: string;
  intro: string;
  content: string;
  featured_image_url: string | null;
}

export function CreateCampaignFromBlog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blogId = searchParams.get('blog');

  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [blogs, setBlogs] = useState<Blog[]>([]);

  useEffect(() => {
    if (user) {
      loadData();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data: blogsData, error: blogsError } = await supabase
        .from('published_blogs')
        .select('id, slug, title, intro, content, featured_image_url')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('published_at', { ascending: false });

      if (blogsError) throw blogsError;

      if (blogsData) {
        setBlogs(blogsData);
        if (blogId) {
          const blog = blogsData.find((b) => b.id === blogId);
          if (blog) {
            setSelectedBlog(blog);
            navigate('/campaign/variant-select', {
              state: { blogId: blog.id },
              replace: true,
            });
          }
        } else {
          const savedBlogId = localStorage.getItem('lastSelectedBlogId');
          if (savedBlogId) {
            const blog = blogsData.find((b) => b.id === savedBlogId);
            if (blog) {
              setSelectedBlog(blog);
              localStorage.removeItem('lastSelectedBlogId');
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <>
        <FocusHeader
          title="Campagne van blog maken"
          onBack={() => navigate('/campaigns')}
        />
        <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
          <LoadingSpinner text="Laden..." />
        </main>
      </>
    );
  }

  return (
    <>
      <FocusHeader
        title="Campagne van blog maken"
        subtitle="Selecteer een blog en laat AI een email genereren"
        onBack={() => navigate('/campaigns')}
      />
      <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
        <div className="space-y-6 max-w-6xl mx-auto">
          <div className="card space-y-6">
            {blogs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">
                  Je hebt nog geen gepubliceerde blogs om een campaign van te maken.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary"
                >
                  Maak je Eerste Blog
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Selecteer Blog
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {blogs.slice(0, 3).map((blog) => (
                    <div key={blog.id} className="relative">
                      <button
                        onClick={() => setSelectedBlog(blog)}
                        className={`
                          w-full relative text-left rounded-lg border-2 transition-all overflow-hidden
                          ${selectedBlog?.id === blog.id
                            ? 'border-[#00B8A9] bg-[#00B8A9]/5 shadow-lg ring-2 ring-[#00B8A9]/20'
                            : 'border-gray-200 bg-white hover:border-[#00B8A9]/50 hover:shadow-md'
                          }
                        `}
                      >
                        {blog.featured_image_url && (
                          <div className="w-full h-40 overflow-hidden">
                            <img
                              src={blog.featured_image_url}
                              alt={blog.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-semibold text-[#231F20] mb-2 line-clamp-2">
                            {blog.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-3">
                            {blog.intro}
                          </p>
                        </div>
                        {selectedBlog?.id === blog.id && (
                          <div className="absolute top-3 right-3 bg-[#00B8A9] text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                            Geselecteerd
                          </div>
                        )}
                      </button>

                      {selectedBlog?.id === blog.id && (
                        <button
                          onClick={() => {
                            navigate('/campaign/variant-select', {
                              state: { blogId: blog.id },
                            });
                          }}
                          className="mt-3 w-full btn-primary inline-flex items-center justify-center gap-2 py-3"
                        >
                          <Wand2 className="w-5 h-5" />
                          Doorgaan naar Variant Selectie
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    onClick={() => navigate('/my-blogs')}
                    className="relative text-left rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:border-[#00B8A9]/50 hover:bg-gray-100 transition-all overflow-hidden p-6 flex flex-col items-center justify-center min-h-[200px] group"
                  >
                    <ArrowRight className="w-8 h-8 text-gray-400 group-hover:text-[#00B8A9] mb-3 transition-colors" />
                    <p className="text-center text-sm font-medium text-gray-700 mb-2">
                      Wil je een oudere blog gebruiken?
                    </p>
                    <p className="text-center text-xs text-gray-500">
                      Dit doe je vanaf je blogoverzicht
                    </p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
